// Транши одной строки: завести, провести, сторно. Сценарий 71.
//
// ## Правки денежной записи не существует
//
// У проведённого транша нет кнопки «Изменить» и не будет. Неверная сумма снимается обратной
// записью с основанием, сторнированное остаётся в списке перечёркнутым, а переключатель
// «действующие / с учётом сторно» решает, показывать ли обе записи.
//
// ## Сторно проведённого транша — ДВА шага, и второй надо назвать
//
// Обратная запись заводится без даты выплаты. Пока её не провели, выплаченное и остаток не
// меняются: сторно висит заведённым, как обычный транш. Промолчать об этом значит показать
// человеку сторно, которое «ничего не сделало», — и он нажмёт ещё раз.
//
// ## Заведённый транш остаток не уменьшает
//
// Это написано прямо в панели, а не оставлено на догадку: заведённый транш выглядит как
// сделанная работа, и без пояснения расхождение остатка читается как ошибка расчёта.

import { useState } from 'react';

import { RiArrowGoBackLine, RiFileTextLine } from '@remixicon/react';
import { Link, useLocation, useNavigate } from 'react-router';

import type { ManualTrancheKind, TrancheOut } from '@/shared/api/endpoints/official';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { can } from '@/shared/rbac/permissions';
import { buildSearch, parseSearch, pushPanel } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDateDMY, formatDateOnly, parseMoment } from '@/shared/time/format';
import * as Button from '@/shared/ui/button';
import * as LinkButton from '@/shared/ui/link-button';
import * as Checkbox from '@/shared/ui/checkbox';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { SelectField } from '@/shared/ui/select-field';
import { ErrorState, Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';
import { cn } from '@/utils/cn';

import { PANEL_OFFICIAL_EMPLOYEE, PANEL_WRITS } from '../../route';
import { useAddTranche, usePayTranche, useRefuseCarry, useReverseTranche } from '../queries';

import { useRowFromGrid, вМоментВыплаты } from './context';

const EDIT_PERMISSION = 'core.official.edit';
const REVERSE_PERMISSION = 'core.official.reverse_tranche';

export function TranchesPanel({ id }: { id: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const права = useAuthStore((state) => state.permissions);
  const { row: строка, period: период, weeks, isPending, error, retry } = useRowFromGrid(id);

  const [сСторно, setССторно] = useState(false);
  const [дата, setДата] = useState(todayIso);
  const [запрет, setЗапрет] = useState<string | null>(null);

  if (error !== null && строка === null) {
    return (
      <ErrorState subject='строку официального периода' error={error as never} onRetry={retry} />
    );
  }
  if (строка === null || период === null) {
    return (
      <div className='flex flex-col gap-2'>
        {isPending ? (
          <>
            <Skeleton className='h-20 w-full rounded-lg' />
            <Skeleton className='h-40 w-full rounded-lg' />
          </>
        ) : (
          <p className='text-paragraph-sm text-text-sub-600'>
            Этой строки в открытом периоде нет. Возможно, ссылка ведёт в другой период — выберите
            его в шапке экрана.
          </p>
        )}
      </div>
    );
  }

  const можноПравить = can(права, EDIT_PERMISSION) && период.can_edit;
  // «Действующие» прячет ПРОВЕДЁННЫЕ обратные записи — они бухгалтерские, работы по ним нет.
  // Сам сторнированный транш остаётся в списке перечёркнутым всегда: удаления денежной записи
  // не существует (правило 8). Непроведённое сторно тоже остаётся всегда — это незакрытая
  // работа, и спрятать её значит спрятать кнопку, которой её заканчивают.
  const видимые = сСторно
    ? строка.tranches
    : строка.tranches.filter((транш) => транш.reversal_of_id === null || транш.paid_at === null);
  const скрыто = строка.tranches.length - видимые.length;
  const непроведённоеСторно = строка.tranches.filter(
    (транш) => транш.reversal_of_id !== null && транш.paid_at === null,
  );

  function открытьДокументы() {
    const состояние = parseSearch(location.search);
    void navigate({
      pathname: location.pathname,
      search: buildSearch(pushPanel(состояние, { view: PANEL_WRITS, id: строка!.employee_id })),
    });
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1'>
        <h3 className='text-label-lg text-text-strong-950'>{строка.employee_name}</h3>
        <span className='text-paragraph-xs text-text-sub-600'>
          {период.title}
          {строка.is_carried_in && (
            <> · строка пришла переносом из {строка.carried_from_title ?? 'прошлого периода'}</>
          )}
        </span>
        {/* Отсюда — во всю его официальную историю. Раньше «сколько ему выплатили за год и по
            какому документу» собиралось переключением периодов в шапке и поиском фамилии в
            каждом: дверь к карточке человека была, а звать её было неоткуда. */}
        <LinkButton.Root asChild variant='primary' size='medium' className='self-start pt-1'>
          <Link
            to={
              location.pathname +
              buildSearch(
                pushPanel(parseSearch(location.search), {
                  view: PANEL_OFFICIAL_EMPLOYEE,
                  id: строка.employee_id,
                }),
              )
            }
          >
            Вся официальная история этого человека
          </Link>
        </LinkButton.Root>
      </div>

      {/* Четыре величины вместе и всегда: убрав «заведено», мы покажем экран, где деньги
          пропали; показав только его — экран, где их выдали дважды. */}
      <FieldList>
        <FieldRow label='К выдаче' value={formatKopecks(строка.payable)} />
        <FieldRow label='Выплачено' value={formatKopecks(строка.paid)} />
        <FieldRow
          label='Заведено, не выдано'
          value={formatKopecks(строка.planned)}
          note={
            строка.planned === 0
              ? undefined
              : 'остаток эта сумма не уменьшает: пока деньги не выданы, человеку должны столько же'
          }
        />
        {/* ⚠️ Переплата печатается ВЕЛИЧИНОЙ: направление уже сказано словом.
            «Переплата −15 127,11» читается как «минус переплата», то есть как недоплата, — и
            рядом, в разборе периода, та же сумма стоит положительной. Два вида одного числа на
            соседних экранах: человек сверяет их и не сходится. */}
        <FieldRow
          label={строка.balance < 0 ? 'Переплата' : 'Остаток'}
          value={formatKopecks(строка.balance < 0 ? Math.abs(строка.balance) : строка.balance)}
          note={
            строка.balance < 0
              ? 'выдали больше начисленного; переносом это не является, разбирается вручную'
              : undefined
          }
        />
        {строка.carried_out !== 0 && (
          <FieldRow label='Перенесено' value={formatKopecks(строка.carried_out)} />
        )}
      </FieldList>

      {непроведённоеСторно.length > 0 && (
        <p className='rounded-lg bg-bg-weak-50 px-3 py-2 text-paragraph-xs text-text-sub-600'>
          {countLabel(непроведённоеСторно.length, [
            'Сторно заведено',
            'Сторно заведены',
            'Сторно заведены',
          ])}
          , но не проведено. Выплаченное и остаток изменятся, когда вы проведёте обратную запись
          датой — как обычный транш.
        </p>
      )}

      {запрет !== null && (
        <p className='rounded-lg bg-bg-weak-50 px-3 py-2 text-paragraph-xs text-warning-base'>
          {запрет}
        </p>
      )}

      <section className='flex flex-col gap-2'>
        <div className='flex items-center justify-between'>
          <h4 className='text-label-sm text-text-strong-950'>Транши · {строка.tranche_count}</h4>
          <button
            type='button'
            onClick={() => setССторно((прежнее) => !прежнее)}
            className='text-paragraph-xs text-text-sub-600 underline underline-offset-2'
          >
            {сСторно ? 'Только действующие' : 'С учётом сторно'}
          </button>
        </div>

        {видимые.length === 0 ? (
          <p className='text-paragraph-sm text-text-sub-600'>
            Траншей нет. Пока их не завели, к выдаче стоит вся сумма целиком.
            {скрыто > 0 && ` Скрыто сторнированных: ${скрыто}.`}
          </p>
        ) : (
          <ul className='flex flex-col gap-1'>
            {видимые.map((транш) => (
              <СтрокаТранша
                key={транш.id}
                транш={транш}
                дата={дата}
                можноПравить={можноПравить}
                можноСторно={can(права, REVERSE_PERMISSION) && период.can_edit}
                onЗапрет={setЗапрет}
              />
            ))}
          </ul>
        )}
        {скрыто > 0 && !сСторно && (
          <p className='text-paragraph-xs text-text-sub-600'>
            Скрыто проведённых обратных записей: {скрыто}. Переключатель выше показывает их.
          </p>
        )}
      </section>

      {можноПравить && (
        <section className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-4'>
          <h4 className='text-label-sm text-text-strong-950'>Провести и завести</h4>
          <DateField
            id='official-paid-at'
            label='Дата выплаты'
            value={дата}
            onChange={setДата}
            hint='Ставится на транш при нажатии «Провести». Заводить транш можно и без даты.'
          />
          <ЗавестиТранш rowId={строка.id} weeks={weeks} дата={дата} />
        </section>
      )}

      <section className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-4'>
        <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={открытьДокументы}>
          <Button.Icon as={RiFileTextLine} />
          Исполнительные документы
        </Button.Root>
        {можноПравить && строка.balance > 0 && (
          <ОтказОтПереноса
            rowId={строка.id}
            version={строка.version}
            остаток={строка.balance}
            основание={строка.carry_refused_basis}
          />
        )}
      </section>
    </div>
  );
}

function СтрокаТранша({
  транш,
  дата,
  можноПравить,
  можноСторно,
  onЗапрет,
}: {
  транш: TrancheOut;
  дата: string;
  можноПравить: boolean;
  можноСторно: boolean;
  onЗапрет: (текст: string | null) => void;
}) {
  const проведение = usePayTranche();
  const сторно = useReverseTranche();
  const [основание, setОснование] = useState('');
  const [форма, setФорма] = useState(false);

  const когда = parseMoment(транш.paid_at);

  return (
    <li className='flex flex-col gap-1 rounded-lg border border-stroke-soft-200 px-3 py-2'>
      <div className='flex items-baseline justify-between gap-2'>
        <span
          className={cn(
            'text-paragraph-sm text-text-strong-950',
            // Сторнированное остаётся в списке перечёркнутым: удаления денежной записи нет.
            транш.is_reversed && 'text-text-sub-600 line-through',
          )}
        >
          {транш.label}
        </span>
        <span
          className={cn(
            'tabular-nums text-label-sm text-text-strong-950',
            транш.is_reversed && 'text-text-sub-600 line-through',
          )}
        >
          {formatKopecks(транш.amount)}
        </span>
      </div>
      <div className='flex items-center justify-between gap-2'>
        <span className='text-paragraph-xs text-text-sub-600'>
          {когда === null ? 'заведён, не выдан' : `выдан ${formatDateDMY(когда)}`}
          {транш.reversal_basis !== null && <> · {транш.reversal_basis}</>}
        </span>
        {транш.paid_at === null && транш.is_reversed === false && (
          <Button.Root
            variant='primary'
            mode='stroke'
            size='xxsmall'
            onClick={() => {
              onЗапрет(null);
              if (!можноПравить) {
                onЗапрет('Проводить транши может финансовый отдел или администратор.');
                return;
              }
              проведение.mutate({ trancheId: транш.id, paidAt: вМоментВыплаты(дата) });
            }}
          >
            {проведение.isPending ? 'Проводим…' : 'Провести'}
          </Button.Root>
        )}
        {!транш.is_reversed && транш.reversal_of_id === null && (
          <Button.Root
            variant='error'
            mode='ghost'
            size='xxsmall'
            onClick={() => {
              onЗапрет(null);
              if (!можноСторно) {
                onЗапрет(
                  'Сторно проведённого транша делают руководство и администратор. Скопируйте ссылку на эту строку и передайте им.',
                );
                return;
              }
              setФорма(true);
            }}
          >
            <Button.Icon as={RiArrowGoBackLine} />
            Сторно
          </Button.Root>
        )}
        {/* Сторно есть и у заведённого, ещё не выданного транша. Иначе транш, заведённый с
            опечаткой, — тупик: убрать его нечем, а непроведённый транш не даёт закрыть период,
            и единственное предложенное действие «провести» означает выдать эти деньги. */}
      </div>
      {форма && (
        <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-2'>
          <TextField
            id={`official-reversal-${транш.id}`}
            label='Основание сторно'
            required
            value={основание}
            onChange={setОснование}
            placeholder='выдали дважды по забытому траншу, возвращаем'
          />
          <p className='text-paragraph-xs text-text-sub-600'>
            Обратная запись на {formatKopecks(-транш.amount)} появится в списке.
            {транш.paid_at === null
              ? ' Транш не выдан, поэтому выплаченного и остатка это не касается — просто снимет обязательство.'
              : ' Пока вы не проведёте её датой, выплаченное и остаток не изменятся.'}
          </p>
          <div className='flex gap-2'>
            <Button.Root
              variant='error'
              mode='filled'
              size='xxsmall'
              onClick={() => {
                if (основание.trim().length < 3) {
                  onЗапрет(
                    'Основание обязательно: без него в журнале останется сторно без причины.',
                  );
                  return;
                }
                onЗапрет(null);
                сторно.mutate(
                  { trancheId: транш.id, basis: основание.trim() },
                  { onSuccess: () => setФорма(false) },
                );
              }}
            >
              {сторно.isPending ? 'Сторнируем…' : 'Сторнировать'}
            </Button.Root>
            <Button.Root
              variant='neutral'
              mode='stroke'
              size='xxsmall'
              onClick={() => setФорма(false)}
            >
              Отмена
            </Button.Root>
          </div>
        </div>
      )}
      {проведение.error !== null && <ErrorAlert error={проведение.error} />}
      {сторно.error !== null && <ErrorAlert error={сторно.error} />}
    </li>
  );
}

function ЗавестиТранш({
  rowId,
  weeks,
  дата,
}: {
  rowId: string;
  weeks: readonly number[];
  дата: string;
}) {
  const операция = useAddTranche();
  const [вид, setВид] = useState<ManualTrancheKind>('main');
  const [неделя, setНеделя] = useState('1');
  const [сумма, setСумма] = useState('');
  const [сразу, setСразу] = useState(false);
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const [ключ, setКлюч] = useState(newIdempotencyKey);
  // Ключ операции привязан к введённому: те же цифры — та же операция (повтор вернёт прежний
  // транш, а не второй), другие цифры — другая. Без этого после неудачи с потерянным ответом
  // сервер отвечает «операция уже была, но с другими данными», а сменить ключ нечем.
  const слепок = `${вид}|${неделя}|${сумма}|${сразу}`;
  const [прежнийСлепок, setПрежнийСлепок] = useState(слепок);
  if (слепок !== прежнийСлепок) {
    setПрежнийСлепок(слепок);
    setКлюч(newIdempotencyKey());
  }

  return (
    <div className='flex flex-col gap-2'>
      <SelectField
        id='official-tranche-kind'
        label='Вид транша'
        value={вид}
        options={[
          { value: 'main', label: 'Основная выплата (15-30 числа)' },
          { value: 'weekly', label: 'Недельный транш' },
        ]}
        onChange={(значение) => setВид(значение as ManualTrancheKind)}
        placeholder='выберите вид'
        hint='Перенос остатка через эту форму не заводится: его делает только закрытие периода.'
      />
      {вид === 'weekly' && (
        <SelectField
          id='official-tranche-week'
          label='Неделя'
          value={неделя}
          options={weeks.map((номер) => ({ value: String(номер), label: `Неделя ${номер}` }))}
          onChange={setНеделя}
          placeholder='выберите неделю'
        />
      )}
      <TextField
        id='official-tranche-amount'
        label='Сумма'
        required
        value={сумма}
        onChange={setСумма}
        placeholder='3 000'
        inputMode='decimal'
        error={ошибка}
      />
      <label className='flex items-center gap-2 text-paragraph-xs text-text-sub-600'>
        <Checkbox.Root
          checked={сразу}
          onCheckedChange={(значение) => setСразу(значение === true)}
        />
        Сразу отметить выданным на {formatDateOnly(дата)}
      </label>
      <p className='text-paragraph-xs text-text-sub-600'>
        {сразу
          ? 'Транш ляжет выплаченным: остаток уменьшится сразу.'
          : 'Транш ляжет заведённым, но не выданным: остаток он НЕ уменьшит, пока вы его не проведёте.'}
      </p>
      <Button.Root
        variant='primary'
        mode='filled'
        size='xsmall'
        onClick={() => {
          const разбор = parseRublesToKopecks(сумма);
          if (!разбор.ok) {
            setОшибка(moneyErrorText(разбор.reason, 'сумма транша'));
            return;
          }
          if (разбор.value <= 0) {
            setОшибка('Сумма транша строго больше нуля: транш на ноль ничего не гасит.');
            return;
          }
          setОшибка(undefined);
          операция.mutate(
            {
              rowId,
              body: {
                kind: вид,
                amount: разбор.value,
                week_no: вид === 'weekly' ? Number(неделя) : null,
                paid_at: сразу ? вМоментВыплаты(дата) : null,
              },
              idempotencyKey: ключ,
            },
            {
              onSuccess: () => {
                // Новый ключ после успеха: следующий транш — другая операция, и повтор прежнего
                // ключа вернул бы прошлую запись вместо новой.
                setКлюч(newIdempotencyKey());
                setСумма('');
              },
            },
          );
        }}
      >
        {операция.isPending ? 'Заводим…' : 'Завести транш'}
      </Button.Root>
      {операция.error !== null && <ErrorAlert error={операция.error} />}
    </div>
  );
}

function ОтказОтПереноса({
  rowId,
  version,
  остаток,
  основание: прежнее,
}: {
  rowId: string;
  version: number;
  остаток: number;
  основание: string | null;
}) {
  const отказ = useRefuseCarry();
  const [основание, setОснование] = useState('');
  const [форма, setФорма] = useState(false);
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);

  if (прежнее !== null) {
    return (
      <p className='rounded-lg bg-bg-weak-50 px-3 py-2 text-paragraph-xs text-warning-base'>
        Перенос остатка отменён: {прежнее}. Эти деньги в следующий период не уйдут.
      </p>
    );
  }

  if (!форма) {
    return (
      <Button.Root variant='neutral' mode='ghost' size='xsmall' onClick={() => setФорма(true)}>
        Не переносить остаток {formatKopecks(остаток)}
      </Button.Root>
    );
  }

  return (
    <div className='flex flex-col gap-2 rounded-lg border border-stroke-soft-200 p-3'>
      <TextField
        id='official-refuse-carry'
        label='Основание отказа'
        required
        value={основание}
        onChange={setОснование}
        placeholder='человек уволился и рассчитан наличными, переносить нечего'
        error={ошибка}
      />
      <p className='text-paragraph-xs text-text-sub-600'>
        {formatKopecks(остаток)} останутся в этом периоде и в следующий не уйдут. Отказ пишется в
        журнал вместе с основанием.
      </p>
      <div className='flex gap-2'>
        <Button.Root
          variant='error'
          mode='stroke'
          size='xxsmall'
          onClick={() => {
            if (основание.trim().length < 3) {
              setОшибка(
                'Основание обязательно: без него отказ от переноса денег ничем не объяснён.',
              );
              return;
            }
            setОшибка(undefined);
            отказ.mutate(
              { rowId, basis: основание.trim(), version },
              { onSuccess: () => setФорма(false) },
            );
          }}
        >
          {отказ.isPending ? 'Отмечаем…' : 'Не переносить'}
        </Button.Root>
        <Button.Root variant='neutral' mode='stroke' size='xxsmall' onClick={() => setФорма(false)}>
          Отмена
        </Button.Root>
      </div>
      {отказ.error !== null && <ErrorAlert error={отказ.error} />}
    </div>
  );
}
